import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import {
  importLawsuitConcluded,
  previewLawsuitConcludedLawyers,
} from "../../services/LawsuitService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import CardHeader from "../General/CardHeader";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

export default function ImportConcludedLawsuit() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ numerosPlanillas: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          numerosPlanillas: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, numerosPlanillas: "" });
        return;
      }
      setErrors({ ...errors, numerosPlanillas: "" });
      setSelectedFile(file);
      setForm({ ...form, numerosPlanillas: file.name });
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("numerosPlanillas", selectedFile);
    return formData;
  };

  const submitFile = async () => {
    setLoading(true);
    try {
      const response = await importLawsuitConcluded(buildFormData());

      showToast(response.data.message, "success");
      setForm({ numerosPlanillas: "" });
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    try {
      await sessionQueue.checkAndRun(
        () => previewLawsuitConcludedLawyers(buildFormData()),
        submitFile
      );
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Carga Numeros de Planillas de Títulos Cargados"
            description="Subir números de planillas de títulos cargados al sistema."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Archivo de Planillas"
                  helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                />

                {errors.numerosPlanillas && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.numerosPlanillas}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading || !online || sessionQueue.isPending}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Cargar Planillas"
                  )}
                </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Número de Planilla SAC"]}
        example={["12345678"]}
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
